import { FC, PropsWithChildren, ReactNode, useState } from 'react';
import { usePostHog } from "@posthog/react";
import { Alert } from "react-daisyui";
import { Link, useNavigate } from "react-router-dom";
import * as z from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Trans, useTranslation } from "react-i18next";

import Routes from "@/router/Routes.ts";
import LegalUrls from "@/_constants/LegalUrls.ts";
import Stepper from "@/components/shared/Stepper/Stepper.tsx";
import { useStepperContext } from "@/components/shared/Stepper/StepperContext.tsx";
import LoadingButton from "@/components/functional/LoadingButton/LoadingButton.tsx";
import AppLogo from "@/components/ui/AppLogo/AppLogo.tsx";
import ContinueWithGoogleButton from "@/components/functional/Oauth/ContinueWithGoogleButton/ContinueWithGoogleButton.tsx";
import ContinueWithFacebookButton from "@/components/functional/Oauth/ContinueWithFacebookButton/ContinueWithFacebookButton.tsx";
import ContinueWithAppleButton from "@/components/functional/Oauth/ContinueWithAppleButton/ContinueWithAppleButton.tsx";
import useRegisterUser from "@/api/useRegisterUser.ts";
import { passToRouteSearchParams } from "@/utils/passToRouteSearchParams.ts";
import { passwordSchema } from "$/utils/validators/passwordSchema.ts";
import RegistrationAccountStatus from "@/_constants/RegistrationAccountStatus.ts";
import { useRegistrationContext } from "@/context/registrationFlowContext.ts";
import useUpdateRegisteringUser from "@/api/useUpdateRegisteringUser.ts";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";

type Schema = SignUpStepSchema;

const steps = ["sign-up", "success-sign-up"] as const;

const RegisterPage: FC = () => {
  return (
    <Stepper.Wrapper steps={steps} initialStep="sign-up">
      <Stepper.Step<typeof steps> step="sign-up">
        <StepSignUp />
      </Stepper.Step>
      <Stepper.Step<typeof steps> step="success-sign-up">
        <StepSuccessSignUp />
      </Stepper.Step>
    </Stepper.Wrapper>
  );
};

export default RegisterPage;

const signUpStepSchema = z
  .object({
    name: z.string().min(1, { message: "This field is required." }),
    last_name: z.string().min(1, { message: "This field is required." }),
    email: z.string().min(1, { message: "This field is required." }).email(
      "Invalid email",
    ),
    password: passwordSchema,
    repeat_password: z.string().min(1, { message: "This field is required." }),
    terms: z.boolean().pipe(z.literal(true, {
      errorMap: () => ({ message: "You must agree to the terms and conditions" }),
    })),
  })
  .superRefine(({ repeat_password, password }, ctx) => {
    if (repeat_password !== password) {
      ctx.addIssue({
        code: "custom",
        message: "The passwords did not match",
        path: ["repeat_password"],
      });
    }
  });

type SignUpStepSchema = z.infer<typeof signUpStepSchema>;

const inputClassName =
  "h-12 w-full rounded-[9px] border border-[var(--cream-3)] bg-white px-4 font-sans text-sm text-[var(--ink)] outline-none transition-[border-color,box-shadow,background-color] duration-300 ease-out placeholder:text-[var(--ink)] placeholder:opacity-50 hover:border-[var(--gold)] focus:border-[var(--gold)] focus:ring-2 focus:ring-[rgba(201,168,76,0.18)]";

type RegistrationFieldProps = {
  children: ReactNode;
  error?: string;
  htmlFor: string;
  label: string;
};

function RegistrationField(
  { children, error, htmlFor, label }: RegistrationFieldProps,
) {
  return (
    <div className="flex flex-col gap-1.5">
      <label
        className="font-sans text-[13px] leading-none text-[var(--text-muted)]"
        htmlFor={htmlFor}
      >
        {label}
      </label>
      {children}
      {error && (
        <p
          className="font-sans text-xs text-red-700"
          id={`${htmlFor}-error`}
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
}

function RegistrationShell({ children }: PropsWithChildren) {
  const { t } = useTranslation();

  return (
    <div className="fixed inset-0 z-50 grid min-h-[100dvh] w-full overflow-hidden bg-[var(--cream)] lg:grid-cols-2">
      <aside className="hidden min-h-[100dvh] flex-col items-center justify-center gap-10 bg-[var(--ink)] px-10 lg:flex">
        <AppLogo.LogoOnly className="w-[170px] max-w-full" />
        <blockquote className="w-[270px] max-w-full rounded-[32px] border-2 border-[var(--gold)] px-7 py-6 text-center font-serif text-lg font-light italic leading-[1.45] text-[var(--gold-lt)] shadow-[0_0_34px_rgba(201,168,76,0.18)]">
          “{t("registrationPage.quote")}”
        </blockquote>
      </aside>

      <section className="flex min-h-[100dvh] overflow-y-auto bg-[var(--cream)]">
        <main className="m-auto w-full max-w-[486px] px-6 py-10 sm:px-8">
          {children}
        </main>
      </section>
    </div>
  );
}

function StepSignUp() {
  const { draftUser, accountStatus, token, saveDraft, setStatus, setToken } =
    useRegistrationContext();
  const posthog = usePostHog();
  const [passValue, setPassValue] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    mutateAsync: registerUser,
    isError: isErrorRegisterUser,
    isPending: isLoadingRegisterUser,
    error: errorRegisterUser,
  } = useRegisterUser();
  const {
    mutateAsync: updateRegisteringUser,
    isError: isErrorUpdateRegisteringUser,
    isPending: isLoadingUpdateRegisteringUser,
    error: errorUpdateRegisteringUser,
  } = useUpdateRegisteringUser();
  const { goToStep } = useStepperContext<typeof steps, Schema>();
  const { control, handleSubmit } = useForm<z.input<typeof signUpStepSchema>, unknown, SignUpStepSchema>({
    resolver: zodResolver(signUpStepSchema),
    mode: "onChange",
    defaultValues: {
      name: draftUser?.name || "",
      last_name: draftUser?.last_name || "",
      email: draftUser?.email || "",
      password: "",
      repeat_password: "",
      terms: draftUser?.terms ?? false,
    },
  });

  const saveNonSensitiveDraft = (data: SignUpStepSchema) => {
    saveDraft({
      name: data.name,
      last_name: data.last_name,
      email: data.email,
      terms: data.terms,
    });
  };

  const handleNextStep = (data: SignUpStepSchema) => {
    void (async () => {
      try {
        if (isLoadingRegisterUser || isLoadingUpdateRegisteringUser) return;

        if (accountStatus === RegistrationAccountStatus.EDITING) {
          if (!token) {
            throw new Error(
              "Registration session has expired. Please restart registration.",
            );
          }

          const response = await updateRegisteringUser({
            token,
            name: data.name,
            last_name: data.last_name,
            email: data.email,
            password: data.password,
            terms: data.terms,
          });

          setToken(response.data.token);
          saveNonSensitiveDraft(data);
          setStatus(RegistrationAccountStatus.CONFIRMING);
          navigate(
            passToRouteSearchParams(Routes.CONFIRM_EMAIL, {
              email: encodeURIComponent(data.email),
            }),
          );
          return;
        }

        const response = await registerUser(data);
        setToken(response.data.token);
        saveNonSensitiveDraft(data);
        posthog?.capture("user_registered", { method: "email" });
        goToStep("success-sign-up");
      } catch (error) {
        console.error(error);
      }
    })();
  };

  const handleClickShowPassword = () => {
    setPassValue(!passValue);
  };

  return (
    <RegistrationShell>
      <form className="flex w-full flex-col" onSubmit={handleSubmit(handleNextStep)} noValidate>
        <h1 className="mb-7 font-serif text-[clamp(36px,4vw,48px)] font-light leading-[0.98] tracking-[-0.02em] text-[var(--ink)]">
          {t('registrationPage.header')}
        </h1>

        <div className="flex flex-col gap-3.5">
          {isErrorRegisterUser && (
            <Alert className="font-sans text-sm" status="error">
              {errorRegisterUser.message}
            </Alert>
          )}
          {isErrorUpdateRegisteringUser && (
            <Alert className="font-sans text-sm" status="error">
              {errorUpdateRegisteringUser.message}
            </Alert>
          )}

          <Controller
            control={control}
            name="name"
            render={({ field, formState: { errors } }) => (
              <RegistrationField
                error={errors.name?.message}
                htmlFor="registration-first-name"
                label={t('label.firstName')}
              >
                <input
                  {...field}
                  aria-describedby={errors.name ? 'registration-first-name-error' : undefined}
                  aria-invalid={Boolean(errors.name)}
                  autoComplete="given-name"
                  className={inputClassName}
                  id="registration-first-name"
                  placeholder={t('placeholder.name')}
                  type="text"
                />
              </RegistrationField>
            )}
          />

          <Controller
            control={control}
            name="last_name"
            render={({ field, formState: { errors } }) => (
              <RegistrationField
                error={errors.last_name?.message}
                htmlFor="registration-last-name"
                label={t('label.lastName')}
              >
                <input
                  {...field}
                  aria-describedby={errors.last_name ? 'registration-last-name-error' : undefined}
                  aria-invalid={Boolean(errors.last_name)}
                  autoComplete="family-name"
                  className={inputClassName}
                  id="registration-last-name"
                  placeholder={t('placeholder.last_name')}
                  type="text"
                />
              </RegistrationField>
            )}
          />

          <Controller
            control={control}
            name="email"
            render={({ field, formState: { errors } }) => (
              <RegistrationField error={errors.email?.message} htmlFor="registration-email" label={t('label.email')}>
                <input
                  {...field}
                  aria-describedby={errors.email ? 'registration-email-error' : undefined}
                  aria-invalid={Boolean(errors.email)}
                  autoComplete="email"
                  className={inputClassName}
                  id="registration-email"
                  inputMode="email"
                  placeholder={t('placeholder.email')}
                  type="email"
                />
              </RegistrationField>
            )}
          />

          <Controller
            control={control}
            name="password"
            render={({ field, formState: { errors } }) => (
              <RegistrationField
                error={errors.password?.message}
                htmlFor="registration-password"
                label={t('label.passwordRegister')}
              >
                <div className="relative">
                  <input
                    {...field}
                    aria-describedby={errors.password ? 'registration-password-error' : undefined}
                    aria-invalid={Boolean(errors.password)}
                    autoComplete="new-password"
                    className={inputClassName}
                    id="registration-password"
                    placeholder={t('placeholder.password')}
                    type={passValue ? 'text' : 'password'}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 flex w-12 items-center justify-center"
                    onClick={handleClickShowPassword}
                  >
                    {passValue ? (
                      <EyeIcon className="h-6 font-extralight" />
                    ) : (
                      <EyeSlashIcon className="h-6 font-extralight" />
                    )}
                  </button>
                </div>
              </RegistrationField>
            )}
          />

          <Controller
            control={control}
            name="repeat_password"
            render={({ field, formState: { errors } }) => (
              <RegistrationField
                error={errors.repeat_password?.message}
                htmlFor="registration-repeat-password"
                label={t('label.repeatPassword')}
              >
                <div className="relative">
                  <input
                    {...field}
                    aria-describedby={errors.repeat_password ? 'registration-repeat-password-error' : undefined}
                    aria-invalid={Boolean(errors.repeat_password)}
                    autoComplete="new-password"
                    className={inputClassName}
                    id="registration-repeat-password"
                    placeholder={t('placeholder.repeat_password')}
                    type={passValue ? 'text' : 'password'}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 flex w-12 items-center justify-center"
                    onClick={handleClickShowPassword}
                  >
                    {passValue ? (
                      <EyeIcon className="h-6 font-extralight" />
                    ) : (
                      <EyeSlashIcon className="h-6 font-extralight" />
                    )}
                  </button>
                </div>
              </RegistrationField>
            )}
          />
        </div>

        <Controller
          control={control}
          name="terms"
          render={({ field: { onChange, value }, formState: { errors } }) => (
            <div className="mt-6 flex flex-col gap-1.5">
              <label className="flex cursor-pointer items-start justify-center gap-3 font-sans text-[13px] leading-5 text-[var(--ink)]">
                <input
                  aria-describedby={errors.terms ? 'registration-terms-error' : undefined}
                  aria-invalid={Boolean(errors.terms)}
                  checked={value}
                  className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer accent-blue-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                  onChange={(event) => onChange(event.target.checked)}
                  type="checkbox"
                />
                <span>
                  <Trans i18nKey="registrationPage.agreeToTerms">
                    I accept the
                    <a
                      className="text-[var(--gold-deep)] transition-colors duration-300 hover:text-[var(--gold)]"
                      target="_blank"
                      rel="noopener noreferrer"
                      href={LegalUrls.PRIVACY}
                    >
                      Privacy Policy
                    </a>
                    and
                    <a
                      className="text-[var(--gold-deep)] transition-colors duration-300 hover:text-[var(--gold)]"
                      target="_blank"
                      rel="noopener noreferrer"
                      href={LegalUrls.TERMS}
                    >
                      Terms &amp; Conditions
                    </a>
                  </Trans>
                </span>
              </label>
              {errors.terms && (
                <p className="text-center font-sans text-xs text-red-700" id="registration-terms-error" role="alert">
                  {errors.terms.message}
                </p>
              )}
            </div>
          )}
        />

        <LoadingButton
          className="mt-7 h-12 min-h-12 w-full rounded-[9px] border-0 !bg-[var(--gold)] font-sans text-sm font-medium normal-case text-[var(--ink)] shadow-[0_8px_24px_rgba(180,146,58,0.16)] transition-[transform,background-color,box-shadow] duration-500 ease-[cubic-bezier(.22,1,.36,1)] hover:!-translate-y-0.5 hover:!bg-[var(--gold-lt)] hover:shadow-[0_12px_28px_rgba(180,146,58,0.24)] active:!translate-y-0 active:!bg-[var(--gold-deep)]"
          loading={isLoadingRegisterUser || isLoadingUpdateRegisteringUser}
          type="submit"
        >
          {accountStatus === RegistrationAccountStatus.EDITING ? t('button.update') : t('button.createAccount')}
        </LoadingButton>

        <div className="mt-3 flex flex-col gap-3">
          {import.meta.env.VITE_GOOGLE_LOGIN_ENABLED === 'true' && (
            <ContinueWithGoogleButton copy={t('button.registerWithGoogle')} />
          )}
          {import.meta.env.VITE_FACEBOOK_LOGIN_ENABLED === 'true' && (
            <ContinueWithFacebookButton copy={t('button.registerWithFacebook')} />
          )}
          {import.meta.env.VITE_APPLE_LOGIN_ENABLED === 'true' && (
            <ContinueWithAppleButton copy={t('button.registerWithApple')} />
          )}
        </div>

        <p className="mt-3 text-center font-sans text-[13px]">
          <span className="text-[var(--ink)]">{t('registrationPage.alreadyHaveAccount')}</span>{' '}
          <Link
            className="text-[var(--gold-deep)] transition-colors duration-300 hover:text-[var(--gold)]"
            to={Routes.LOGIN}
          >
            {t('button.signIn')}
          </Link>
        </p>
      </form>
    </RegistrationShell>
  );
}

function StepSuccessSignUp() {
  const { t } = useTranslation();
  const { previousStep } = useStepperContext<typeof steps, Schema>();
  const { draftUser, setStatus } = useRegistrationContext();

  return (
    <div className="fixed inset-0 z-50 flex min-h-[100dvh] overflow-y-auto bg-[var(--cream)] px-6 py-10">
      <main className="m-auto flex w-full max-w-[650px] flex-col items-center text-center">
        <h1
          className="font-serif text-[clamp(42px,5vw,52px)] leading-none tracking-[-0.02em] text-[var(--ink)]"
          style={{ fontWeight: 400 }}
        >
          {t("registrationPage.congrats")}
        </h1>
        <p className="mt-8 max-w-[620px] font-sans text-base leading-[1.55] text-[var(--text-muted)] sm:text-lg">
          {t("registrationPage.congratsText")}
        </p>

        <Link
          className="journi-primary-button flex h-14 w-full items-center justify-center rounded-[12px] border border-[var(--gold-deep)] bg-[var(--gold)] font-sans text-base font-medium text-[var(--ink)] shadow-[0_8px_24px_rgba(180,146,58,0.16)] transition-[transform,background-color,box-shadow] duration-500 ease-[cubic-bezier(.22,1,.36,1)] hover:-translate-y-0.5 hover:bg-[var(--gold-lt)] hover:shadow-[0_12px_28px_rgba(180,146,58,0.24)] active:translate-y-0 active:bg-[var(--gold-deep)]"
          style={{ marginTop: "3rem" }}
          to={passToRouteSearchParams(Routes.CONFIRM_EMAIL, {
            email: encodeURIComponent(draftUser?.email as string),
          })}
          onClick={() => setStatus(RegistrationAccountStatus.CONFIRMING)}
        >
          {t("registrationPage.gotIt")}
        </Link>

        <button
          className="mt-8 rounded-md px-4 py-2 font-sans text-lg font-medium text-[var(--ink)] transition-colors duration-300 hover:text-[var(--gold-deep)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--gold)]"
          onClick={() => {
            setStatus(RegistrationAccountStatus.EDITING);
            previousStep();
          }}
          type="button"
        >
          {t("button.goBack")}
        </button>
      </main>
    </div>
  );
}
